import { useState, useEffect, useMemo, useRef } from 'react'
import cutileiApi from 'services/cutileiApi'
import { connectSocket, SocketRoom } from 'services/socket'
import { useNavigate } from 'react-router-dom'
import AuthService from 'services/auth'
import TimeWindow from 'services/timeWindow'
import ScheduleGridService from 'services/ScheduleGrid'
import { Calendar } from '@cutilei/ui'
import ReactTooltip from 'react-tooltip'
import BusinessNavbar from 'components/Navbars/BusinessNavbar'
import ItemTabs from 'components/ItemTabs'
import Tutorial from 'components/Tutorial'
import TextInput from 'components/Inputs/TextInput'
import DayGrid from 'components/Grids/DayGrid'
import WeekGrid from 'components/Grids/WeekGrid'
import Select from 'components/Inputs/Select'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import DateReservation from 'components/DateReservation'
import CreateReservationModal from '../Modals/CreateReservationModal'
import SelectBusinessModal from '../Modals/SelectBusinessModal'
import ScheduleLinkModal from '../Modals/ScheduleLinkModal'
import ChangePictureModal from '../Modals/ChangePictureModal'
import InactiveBusinessModal from '../Modals/InactiveBusinessModal'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { FaLink, FaUserPlus, FaChevronDown, FaStar } from 'react-icons/fa'
import { IoCalendarOutline } from 'react-icons/io5'
import { IconContext } from 'react-icons'
import { DateTime } from 'luxon'

import {
  Container,
  Row,
  Column,
  Header,
  ImageButton,
  Image,
  SubTitle,
  TooltipLabel,
  SubContainer,
  BusinessContainer,
  RatingContainer,
  CalendarContainer,
  LoadingBackground,
  Schedules
} from './styles'

export default function Agenda () {
  const [query, setQuery] = useState ('')
  const [selectedDate, setSelectedDate] = useState (new Date ())
  const [prevDate, setPrevDate] = useState (new Date ())
  const selectedDateRef = useRef (selectedDate)
  const scrollPosition = useRef (0)
  const [business, setBusiness] = useState (null)
  const [branches, setBranches] = useState ([])
  const [businessConfig, setBusinessConfig] = useState ([])
  const [workingDays, setWorkingDays] = useState ([])
  const [dateReservations, setDateReservations] = useState ([])
  const [exceptionalDates, setExceptionalDates] = useState ([])
  const [professionals, setProfessionals] = useState ([])
  const [scheduleStatuses, setScheduleStatuses] = useState ([])
  const [selectedProfessional, setSelectedProfessional] = useState (null)
  const [schedules, setSchedules] = useState ([])
  const [loadingData, setLoadingData] = useState (true)
  const [loadingSchedules, setLoadingSchedules] = useState (true)
  const [layout, setLayout] = useState ('day_schedules')
  const [validatingConfig, setValidatingConfig] = useState (true)
  const [showTutorial, setShowTutorial] = useState (false)
  const [showPictureDialog, setShowPictureDialog] = useState (false)
  const [showInactiveDialog, setShowInactiveDialog] = useState (false)
  const [showBusinessesModal, setShowBusinessesModal] = useState (false)
  const [showCreateReservationModal, setShowCreateReservationModal] = useState (false)
  const [showScheduleLinkModal, setShowScheduleLinkModal] = useState (false)
  const user = AuthService.getUser ()
  const token = AuthService.getToken ()
  const isAdmin = AuthService.isAdmin ()
  const isBusiness = AuthService.isBusiness ()
  const businessId = AuthService.getBusinessId ()
  const hasEditPermission = AuthService.hasPermission ('agenda', 'edit')
  const navigate = useNavigate ()

  const enableBranchSwitch = useMemo (() => (
    (isBusiness || isAdmin) && branches.length > 1
  ), [isBusiness, isAdmin, branches])

  const OPTION_LIST = [
    {label: 'Dia', value: 'day_schedules'},
    {label: 'Semana', value: 'week_schedules'}
  ]

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    const socket = connectSocket ({
      token, rooms: [SocketRoom.business (businessId)]
    })

    const socketEvents = [
      { event: `${businessId}_schedule_created`, handler: schedule => handleCreateSchedule ([schedule]) },
      { event: `${businessId}_reservation_created`, handler: handleCreateReservations },
      { event: `${businessId}_schedule_updated`, handler: handleEditSchedule },
      { event: `${businessId}_reservation_updated`, handler: handleEditReservation },
      { event: `${businessId}_schedule_status_updated`, handler: handleEditScheduleStatus },
      { event: `${businessId}_schedule_deleted`, handler: handleDeleteSchedule },
      { event: `${businessId}_professional_removed`, handler: handleRemoveProfessional }
    ]

    socketEvents.forEach (({ event, handler }) => socket.on (event, handler))

    return () => {
      socketEvents.forEach (({ event }) => socket.off (event))
      // socket.off remove os listeners mas mantem a conexao aberta.
      socket.disconnect ()
    }
  }, [businessId, token])

  useEffect (() => {
    validateConfig ()
  }, [showTutorial])

  useEffect (() => {
    selectedDateRef.current = selectedDate
    if (businessConfig.isComplete) getData (selectedDate)
  }, [selectedDate, layout, businessConfig.isComplete])

  useEffect (() => {
    selectedDateRef.current = selectedDate
    if (businessConfig.isComplete && selectedProfessional) getSchedules ()
  }, [selectedDate, layout, selectedProfessional, businessConfig.isComplete])

  const timeInterval = useMemo (() => {
    const schedulesBaseInterval = schedules.some (s => s.duration && s.duration % 30 !== 0) ? 15 : 30
    return Math.min (businessConfig.timeInterval, schedulesBaseInterval)
  }, [schedules, businessConfig.timeInterval])

  const timeWindows = useMemo (() => {
    if (loadingData || loadingSchedules || workingDays.length === 0) return []

    const selectedWeekday = DateTime.fromJSDate (selectedDate).weekday
    const workingDay = workingDays.find (day => day.iso_number === selectedWeekday)

    return ScheduleGridService.getDayTimeWindows ({
      schedules,
      opening: workingDay?.opening || workingDays[0].opening,
      closing: workingDay?.closing || workingDays[0].closing,
      timeInterval
    })
  }, [loadingData, loadingSchedules, workingDays, schedules, selectedDate, timeInterval])

  const validateConfig = async () => {
    try {
      const parentId = user?.parent_id || businessId

      const [{ data: business }, { data: branches }, { data: businessConfig }] = await Promise.all ([
        cutileiApi.get (`/businesses/${businessId}`),
        cutileiApi.get (`/businesses?parent_id=${parentId}&order=oldest`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/validate_config`, requestConfig)
      ])

      setBusiness (business)
      setBranches (branches.data)
      setBusinessConfig (businessConfig)
      setShowInactiveDialog (!businessConfig.isBusinessActive)
      setShowTutorial (!businessConfig.isComplete)
    } catch (error) {
      console.error (error)
    } finally {
      setValidatingConfig (false)
    }
  }

  const getData = async date => {
    setLoadingData (true)
    try {
      const selectedISODate = DateTime.fromJSDate (date).toISODate ()
      const filters = `access_level=service&status=active&date=${selectedISODate}`

      const [
        { data: professionals },
        { data: workingDays },
        { data: dateReservations },
        { data: exceptionalDates },
        { data: scheduleStatuses }
      ] = await Promise.all ([
        cutileiApi.get (`/businesses/${businessId}/professionals?${filters}`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/working_days`),
        cutileiApi.get (`/businesses/${businessId}/date_reservations?date=${selectedISODate}`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/exceptional_dates?date=${selectedISODate}`, requestConfig),
        cutileiApi.get (`/schedule_statuses?business_id=${businessId}`, requestConfig)
      ])

      setProfessionals (professionals)
      setWorkingDays (workingDays)
      setDateReservations (dateReservations)
      setExceptionalDates (exceptionalDates)
      setScheduleStatuses (scheduleStatuses)

      setSelectedProfessional (prev => {
        if (prev && professionals.some (p => p.id === prev.value?.id)) return prev

        return professionals[0] ? {
          label: professionals[0].nickname || professionals[0].name.split (' ')[0],
          value: professionals[0]
        } : null
      })
    } catch (error) {
      setProfessionals ([])
      setWorkingDays ([])
      setSelectedProfessional (null)
      console.error (error)
    } finally {
      setLoadingData (false)
    }
  }

  const getSchedules = async () => {
    setLoadingSchedules (true)

    const today = DateTime.local ().toISODate ()
    const selectedISODate = DateTime.fromJSDate (selectedDate).toISODate ()
    const prevISODate = DateTime.fromJSDate (prevDate).toISODate ()

    if (prevISODate === today) scrollPosition.current = window.scrollY
    
    const weekDays = TimeWindow.getWeekDaysFromDate (selectedISODate)
    const weekStartDate = weekDays[0].toISODate ()
    const weekEndDate = weekDays.slice(-1)[0].toISODate ()

    const filters = 'show_all_professionals=true&' + (
      layout === 'day_schedules'
        ? `date=${selectedISODate}`
        : `start_date=${weekStartDate}&end_date=${weekEndDate}&professional_id=${selectedProfessional.value.id}`
    )
    
    try {
      const [{ data: schedules }, { data: reservations }] = await Promise.all ([
        cutileiApi.get (`/schedules?business_id=${businessId}&active=true&page=1&per_page=999&${filters}`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/schedule_reservations?${filters}`, requestConfig)
      ])

      setSchedules ([...schedules.data, ...reservations].sort (compareSchedules))
    } catch (error) {
      setSchedules ([])
      console.error (error)
    } finally {
      setLoadingSchedules (false)
      if (selectedISODate === today) window.scrollTo (0, scrollPosition.current)
    }
  }

  const filterSchedulesByCustomer = schedules => {
    return query === '' ? schedules : schedules.filter (schedule => (
      schedule.validated !== undefined && (
        schedule.member?.name.toLowerCase ().includes (query.toLowerCase ()) ||
        schedule.customer?.name.toLowerCase ().includes (query.toLowerCase ())
      )
    ))
  }

  // DateTime.fromISO exige hora com dois dígitos: '8:00' vira DateTime inválido e
  // .toMillis() vira NaN, então o comparador devolvia NaN e a ordem do array ficava
  // indefinida para todo item antes das 10h. Como nenhum bloco define grid-column-start,
  // é essa ordem que decide de que lado da divisão 50/50 cada item cai.
  const compareSchedules = (a, b) => {
    const aStart = ScheduleGridService.getScheduleRange(a).start
    const bStart = ScheduleGridService.getScheduleRange(b).start

    if (!aStart.isValid || !bStart.isValid) return 0

    return aStart.toMillis() - bStart.toMillis()
  }

  const handleChangePicture = newPictureUrl => {
    AuthService.updateUser ({business: {...user, logo: newPictureUrl}})
    togglePictureDialog ()
  }

  const handleCreateSchedule = createdSchedules => {
    if (createdSchedules[0].date === DateTime.fromJSDate (selectedDateRef.current).toISODate ()) {
      setSchedules (prevSchedules => {
        const updatedSchedules = [...prevSchedules]
      
        createdSchedules.forEach (newSchedule => {
          if (!updatedSchedules.some (existingSchedule => existingSchedule.id === newSchedule.id))
            updatedSchedules.push (newSchedule)
        })
    
        return updatedSchedules.sort (compareSchedules)
      })
    }
  }

  const handleCreateReservations = reservations => {
    const currentDate = DateTime.fromJSDate (selectedDateRef.current).startOf ('day')

    setSchedules (prevSchedules => {
      const updatedSchedules = [...prevSchedules]
    
      reservations.forEach (reservation => {
        const startDate = DateTime.fromISO (reservation.start_date).startOf ('day')
        const endDate = DateTime.fromISO (reservation.end_date).startOf ('day')

        if (
          startDate <= currentDate && endDate >= currentDate
          && !updatedSchedules.some (existingSchedule => existingSchedule.id === reservation.id)
        ) updatedSchedules.push (reservation)
      })
  
      return updatedSchedules.sort (compareSchedules)
    })
  }

  const handleEditReservation = reservation => {
    const startDate = DateTime.fromISO (reservation.start_date).startOf ('day')
    const endDate = DateTime.fromISO (reservation.end_date).startOf ('day')
    const currentDate = DateTime.fromJSDate (selectedDateRef.current).startOf ('day')

    setSchedules (prevSchedules => [
      ...prevSchedules.filter (r => r.id !== reservation.id),
      ...(startDate <= currentDate && endDate >= currentDate ? [reservation] : [])
    ].sort (compareSchedules))
  }

  const handleEditDateReservation = reservation => {
    const startDate = DateTime.fromISO (reservation.start_date).startOf ('day')
    const endDate = DateTime.fromISO (reservation.end_date).startOf ('day')
    const currentDate = DateTime.fromJSDate (selectedDateRef.current).startOf ('day')
    const isExceptionalDate = !!reservation.start_time

    const updateData = prev => [
      ...prev.filter (r => r.id !== reservation.id),
      ...(startDate <= currentDate && endDate >= currentDate ? [reservation] : [])
    ]

    if (isExceptionalDate) setExceptionalDates (prev => updateData (prev))
    else setDateReservations (prev => updateData (prev))
  }

  const handleEditSchedule = schedule => {
    const scheduleDate = DateTime.fromISO (schedule.date).startOf ('day')
    const currentDate = DateTime.fromJSDate (selectedDateRef.current).startOf ('day')

    setSchedules (prevSchedules => [
      ...prevSchedules.filter (s => s.id !== schedule.id),
      ...(scheduleDate.equals (currentDate) ? [schedule] : [])
    ].sort (compareSchedules))
  }

  const handleEditScheduleStatus = editedSchedules => {
    if (editedSchedules[0]?.date === DateTime.fromJSDate (selectedDateRef.current).toISODate ())
      setSchedules (prevSchedules => [
        ...prevSchedules.filter (schedule => !editedSchedules.some (s => s.id === schedule.id)),
        ...editedSchedules
      ].sort (compareSchedules))
  }

  /*
    A divisão devolve o agendamento original já alterado e o novo, então o mesmo merge resolve
    os dois casos: filtra os ids recebidos e reinsere a lista inteira.
  */
  const handleSplitSchedule = splitSchedules => {
    if (splitSchedules[0]?.date !== DateTime.fromJSDate (selectedDateRef.current).toISODate ())
      return

    setSchedules (prevSchedules => [
      ...prevSchedules.filter (schedule => !splitSchedules.some (s => s.id === schedule.id)),
      ...splitSchedules
    ].sort (compareSchedules))
  }

  const handleDeleteSchedule = schedule => {
    setSchedules (prevSchedules => prevSchedules.filter (s => s.id !== schedule.id))
  }
  
  const handleDeleteAllSchedules = deletedSchedules => {
    setSchedules (prevSchedules => (
      prevSchedules.filter (schedule => !deletedSchedules.some (s => s.id === schedule.id))
    ))
  }

  const handleRemoveProfessional = professional => {
    setProfessionals (prevProfessionals => prevProfessionals.filter (p => p.id !== professional.id))
    setSchedules (prevSchedules => prevSchedules.filter (s => s.professional.id !== professional.id))
  }

  const handleSelectBusiness = selectedBusiness => {
    AuthService.updateUser ({
      business: {
        ...user,
        parent_id: business.parent_id ? null : business.id,
        id: selectedBusiness.id,
        logo: selectedBusiness.logo,
        name: selectedBusiness.name
      }
    })

    window.location.reload ()
  }

  const toggleBusinessesModal = () => setShowBusinessesModal (!showBusinessesModal)
  const toggleCreateReservationModal = () => setShowCreateReservationModal (!showCreateReservationModal)
  const toggleScheduleLinkModal = () => setShowScheduleLinkModal (!showScheduleLinkModal)
  const togglePictureDialog = () => setShowPictureDialog (!showPictureDialog)
  const toggleInactiveDialog = () => setShowInactiveDialog (!showInactiveDialog)
  const handleTutorialCompleted = () => setShowTutorial (false)

  const renderBusinessContainer = () => (
    <BusinessContainer
      clickable={enableBranchSwitch}
      onClick={() => {
        if (enableBranchSwitch) toggleBusinessesModal ()
      }}
    >
      {isBusiness ? (
        <ImageButton onClick={e => {
          e.stopPropagation ()
          togglePictureDialog ()
        }}>
          <Image
            src={isBusiness ? user.logo : user.profile_picture}
            alt={user.name}
            data-for='changePicture'
            data-tip
          />
          {business?.ratings_count > 0 && (
            <RatingContainer>
              <FaStar size={12}/>
              <SubTitle>{business?.average_rating}</SubTitle>
            </RatingContainer>
          )}
          <ReactTooltip
            id='changePicture'
            effect='solid'
            place='right'
            backgroundColor='#252525'
            tooltipRadius='10'
          >
            <TooltipLabel>Alterar foto</TooltipLabel>
          </ReactTooltip>
        </ImageButton>
      ) : (
        <Image
          src={isBusiness ? user.logo : user.profile_picture}
          alt={user.name}
        />
      )}
      <SubTitle style={{ flex: 1 }}>
        {user.name}
      </SubTitle>
      {enableBranchSwitch && <FaChevronDown size={18}/>}
    </BusinessContainer>
  )

  return (
    <IconContext.Provider value={{ color: '#FFC100' }}>
      <Container>
        <BusinessNavbar/>
        {validatingConfig ? (
          <Loading style={{ marginTop: 10, marginLeft: 20 }}/>
        ) : showTutorial ? (
          <Column>
            {renderBusinessContainer ()}
            <Tutorial
              businessConfig={businessConfig}
              onCompleted={handleTutorialCompleted}
            />
          </Column>
        ) : (
          <SubContainer>
            <Header/>
            <CalendarContainer>
              {renderBusinessContainer ()}
              <ItemTabs
                tabs={OPTION_LIST}
                selectedTab={OPTION_LIST.find (o => o.value === layout)?.value}
                onTabChange={setLayout}
                containerStyle={{padding: 0}}
              />
              <Row>
                <TextInput
                  value={query}
                  placeholder='Pesquisar por cliente'
                  isClearable={true}
                  onChange={e => setQuery (e.target.value)}
                  onClear={() => setQuery ('')}
                  containerStyle={{ width: '100%' }}
                  inputStyle={{ boxShadow: '0px 1px 10px -6px', paddingInline: 12 }}
                  data-for='search'
                  data-tip
                />
                <ReactTooltip
                  id='search'
                  effect='solid'
                  place='right'
                  backgroundColor='#252525'
                  tooltipRadius='10'
                >
                  <TooltipLabel>
                    Pesquisar agendamentos por nome do cliente
                  </TooltipLabel>
                </ReactTooltip>
              </Row>
              <Row>
                {/* O Calendar do @cutilei/ui, com a marca de hoje: negrito e um ponto verde. */}
                <Calendar
                  value={selectedDate}
                  onValueChange={date => {
                    setPrevDate(selectedDate)
                    setSelectedDate(date)
                  }}
                  highlightToday
                />
              </Row>
              {[...dateReservations, ...exceptionalDates].map (reservation => (
                <DateReservation
                  key={reservation.id}
                  data={reservation}
                  onEdit={handleEditDateReservation}
                />
              ))}
              {layout === 'week_schedules' && (
                <Row>
                  <Select
                    name='professional'
                    placeholder='Profissional...'
                    value={selectedProfessional}
                    options={professionals.map (professional => ({
                      label: professional.nickname,
                      value: professional
                    }))}
                    onChange={(_, value) => setSelectedProfessional (value)}
                  />
                </Row>
              )}
              {isBusiness && (
                <SecondaryButton
                  label='Cadastrar profissional'
                  leftIcon={<FaUserPlus size={16}/>}
                  onClick={() => navigate ('/business/add-professional')}
                  style={{width: '100%'}}
                />
              )}
              {hasEditPermission && (
                <SecondaryButton
                  label='Reservar agenda'
                  leftIcon={<IoCalendarOutline size={16}/>}
                  onClick={toggleCreateReservationModal}
                  style={{width: '100%'}}
                />
              )}
              <SecondaryButton
                label='Agendar por link'
                leftIcon={<FaLink size={14}/>}
                onClick={toggleScheduleLinkModal}
                style={{width: '100%'}}
              />
            </CalendarContainer>
            <Schedules>
              {loadingData || loadingSchedules || timeWindows.length === 0 ? (
                <LoadingBackground>
                  <Loading/>
                </LoadingBackground>
              ) : layout === 'day_schedules' ? (
                <DayGrid
                  professionals={professionals}
                  schedules={filterSchedulesByCustomer (schedules)}
                  scheduleStatuses={scheduleStatuses}
                  workingDays={workingDays}
                  timeWindows={timeWindows}
                  timeInterval={timeInterval}
                  date={selectedDate}
                  numRows={timeWindows.length}
                  numColumns={professionals.length}
                  query={query}
                  onScheduleCreate={handleCreateSchedule}
                  onEditSchedule={handleEditSchedule}
                  onEditReservation={handleEditReservation}
                  onDeleteSchedule={handleDeleteSchedule}
                  onDeleteAllSchedules={handleDeleteAllSchedules}
                  onStatusListUpdated={setScheduleStatuses}
                  onEditScheduleStatus={handleEditScheduleStatus}
                  onSplitSchedule={handleSplitSchedule}
                  onRemoveProfessional={handleRemoveProfessional}
                />
              ) : (
                <WeekGrid
                  professional={selectedProfessional.value}
                  schedules={filterSchedulesByCustomer (schedules)}
                  scheduleStatuses={scheduleStatuses}
                  workingDays={workingDays}
                  timeWindows={timeWindows}
                  timeInterval={timeInterval}
                  date={selectedDate}
                  query={query}
                  onScheduleCreate={handleCreateSchedule}
                  onEditSchedule={handleEditSchedule}
                  onEditReservation={handleEditReservation}
                  onDeleteSchedule={handleDeleteSchedule}
                  onDeleteAllSchedules={handleDeleteAllSchedules}
                  onStatusListUpdated={setScheduleStatuses}
                  onEditScheduleStatus={handleEditScheduleStatus}
                  onSplitSchedule={handleSplitSchedule}
                  onRemoveProfessional={handleRemoveProfessional}
                />
              )}
            </Schedules>
            <CreateReservationModal
              visible={showCreateReservationModal}
              timeInterval={timeInterval}
              onClose={toggleCreateReservationModal}
              onConfirm={handleCreateReservations}
            />
            <ScheduleLinkModal
              visible={showScheduleLinkModal}
              onlineScheduleEnabled={businessConfig.isOnlineScheduleEnabled}
              onClose={toggleScheduleLinkModal}
            />
            <ChangePictureModal
              title='Alterar foto de perfil'
              visible={showPictureDialog}
              onConfirm={handleChangePicture}
              onClose={togglePictureDialog}
            />
          </SubContainer>
        )}
        <SelectBusinessModal
          visible={showBusinessesModal}
          businesses={branches}
          selectedBusiness={business}
          onConfirm={handleSelectBusiness}
          onClose={toggleBusinessesModal}
        />
        <InactiveBusinessModal
          visible={showInactiveDialog && !isAdmin}
          status={businessConfig.businessStatus}
          onClose={toggleInactiveDialog}
        />
      </Container>
    </IconContext.Provider>
  )
}
